/**
 * @file: ChatContactPickerView.tsx
 *
 * Contact picker content — navigation-agnostic, mode-driven (`useContactPicker`):
 *  - `direct`: single-tap a colleague to resolve (open a 1:1) — no checkboxes,
 *    no submit; the header is just a title + close.
 *  - `new-group` / `invite`: multi-select with a 取消 / 完成 header; reports the
 *    outcome via `onDone`.
 * The host decides what to navigate to. Embeddable in a panel (`embedded`,
 * drops the screen safe-area) or mounted full-screen by the picker screen.
 */

import { Typography } from '@euler/app/components/typography';
import {
  ChatContactPickerParams,
  ChatPickerDoneResult,
} from '@euler/app/flows/chat/lib/types';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatContact } from '@euler/model/chat';
import { Search, X } from 'lucide-react-native';
import { FC, memo, useCallback } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useContactPicker } from '../functions/useContactPicker';
import { ContactRow, ContactRowSeparator } from './ContactRow';

export const ChatContactPickerView: FC<{
  params: ChatContactPickerParams;
  onDone: (result: ChatPickerDoneResult) => void;
  onCancel: () => void;
  /** Mounted inside a panel — drop the screen safe-area padding. */
  embedded?: boolean;
}> = memo(({ params, onDone, onCancel, embedded }) => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const {
    query,
    setQuery,
    visibleContacts,
    isDirect,
    selectedIds,
    selectedCount,
    lockedMemberId,
    busy,
    headerTitle,
    toggleContact,
    onSubmit,
  } = useContactPicker(params, onDone);

  const renderItem: ListRenderItem<ChatContact> = useCallback(
    ({ item }) => (
      <ContactRow
        contact={item}
        showCheck={!isDirect}
        checked={selectedIds.has(item.memberId)}
        locked={lockedMemberId === item.memberId}
        onPress={() => toggleContact(item.memberId)}
      />
    ),
    [isDirect, selectedIds, lockedMemberId, toggleContact],
  );

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: '#fff',
        paddingTop: embedded ? 0 : insets.top,
      }}
    >
      {/* Header — this surface hides the navigator header. Direct mode is a
          plain title + close; multi-select adds 取消 / 完成. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          height: rpx(56),
          paddingHorizontal: rpx(16),
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: '#ececec',
          gap: rpx(8),
        }}
      >
        {isDirect ? (
          <>
            <View style={{ width: rpx(24) }} />
            <Typography.Text
              size={rpx(16)}
              color="#111"
              style={{
                flex: 1,
                textAlign: 'center',
                fontFamily: FontFamily.NotoSans.Bold,
              }}
            >
              {headerTitle}
            </Typography.Text>
            <TouchableOpacity
              onPress={onCancel}
              hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              style={{ paddingHorizontal: rpx(4) }}
            >
              <X size={rpx(20)} color="#6b7280" />
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TouchableOpacity
              onPress={onCancel}
              hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              style={{ paddingHorizontal: rpx(4) }}
            >
              <Typography.Text size={rpx(15)} color="#3b82f6">
                取消
              </Typography.Text>
            </TouchableOpacity>
            <Typography.Text
              size={rpx(16)}
              color="#111"
              style={{
                flex: 1,
                textAlign: 'center',
                fontFamily: FontFamily.NotoSans.Bold,
              }}
            >
              {headerTitle}
              {selectedCount > 0 ? ` (${selectedCount})` : ''}
            </Typography.Text>
            <TouchableOpacity
              onPress={onSubmit}
              disabled={busy || selectedCount === 0}
              hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              style={{
                paddingHorizontal: rpx(10),
                paddingVertical: rpx(4),
                borderRadius: rpx(6),
                backgroundColor:
                  busy || selectedCount === 0 ? '#e5e7eb' : '#3b82f6',
              }}
            >
              {busy ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Typography.Text
                  size={rpx(14)}
                  color={selectedCount === 0 ? '#9ca3af' : '#fff'}
                  style={{ fontFamily: FontFamily.NotoSans.Medium }}
                >
                  完成
                </Typography.Text>
              )}
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* Search bar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          margin: rpx(12),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(8),
          backgroundColor: '#f3f4f6',
          borderRadius: rpx(8),
          gap: rpx(8),
        }}
      >
        <Search size={rpx(16)} color="#9ca3af" />
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder="搜索同事"
          placeholderTextColor="#9ca3af"
          returnKeyType="search"
          style={{
            flex: 1,
            fontSize: rpx(14),
            color: '#111',
            fontFamily: FontFamily.NotoSans.Regular,
            padding: 0,
          }}
        />
        {query.length > 0 ? (
          <TouchableOpacity
            onPress={() => setQuery('')}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          >
            <X size={rpx(16)} color="#9ca3af" />
          </TouchableOpacity>
        ) : null}
      </View>

      <FlatList
        data={visibleContacts}
        keyExtractor={c => String(c.memberId)}
        renderItem={renderItem}
        ItemSeparatorComponent={ContactRowSeparator}
        keyboardShouldPersistTaps="handled"
        // iOS: inset the list for the keyboard so the bottom rows aren't stuck
        // under it (the search field stays focused while you pick). No-op on
        // Android, which resizes the window itself. (No keyboardDismissMode —
        // scrolling should move the list, not dismiss the keyboard.)
        automaticallyAdjustKeyboardInsets
        contentContainerStyle={{ paddingBottom: embedded ? 0 : insets.bottom }}
        ListEmptyComponent={
          <View style={{ padding: rpx(40), alignItems: 'center' }}>
            <Typography.Text size={rpx(13)} color="#9ca3af">
              {query
                ? '没有匹配的联系人'
                : isDirect
                ? '暂无联系人'
                : '没有可邀请的成员'}
            </Typography.Text>
          </View>
        }
      />
    </View>
  );
});
